// ** React Imports
import type { ElementType } from 'react';

// ** Next Imports
import Link from 'next/link';
import { useRouter } from 'next/router';

// ** Custom Components Imports
import CanViewNavLink from '../../CanViewNavLink';
import FaIconUvilo, { type IconPrefix } from '@erikdakoda/ui/components/FaIconUvilo';

// ** Configs Import
import themeConfig from '../../../../config';
import type { Settings } from '../../../../context/settingsContext';
// ** Types
import type { NavGroup, NavLink } from '../../../../types';
// ** Util Import
import { handleURLQueries } from '../../../../layouts/utils';
// ** MUI Imports
import Box, { type BoxProps } from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MuiListItem, { type ListItemProps } from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { hexToRGBA } from '@erikdakoda/ui/shared/colorUtils';
import {
  collapsedNavItemInsetPx,
  hiddenNavLabelSx,
  NAV_ITEM_ICON_WIDTH_PX,
  NAV_ITEM_ICON_LABEL_GAP,
} from './navItemLayout';

interface Props {
  parent?: boolean;
  item: NavLink;
  navHover?: boolean;
  settings: Settings;
  navVisible?: boolean;
  collapsedNavWidth: number;
  navigationBorderWidth: number;
  toggleNavVisibility: () => void;
  isSubToSub?: NavGroup | undefined;
}

const MenuItemTextMetaWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  transition: 'opacity .25s ease-in-out',
  ...(themeConfig.config.menuTextTruncate && { overflow: 'hidden' }),
}));

const VerticalNavLink = ({
  item,
  parent,
  navHover,
  settings,
  navVisible,
  isSubToSub,
  collapsedNavWidth,
  toggleNavVisibility,
}: Props) => {
  'use memo';

  // ** Hooks
  const router = useRouter();

  // ** Vars
  const { navCollapsed } = settings;
  const hideNavLabels = navCollapsed && !navHover;
  const collapsedIconInsetPx = collapsedNavItemInsetPx(collapsedNavWidth, NAV_ITEM_ICON_WIDTH_PX);

  const icon = parent && !item.icon ? themeConfig.config.navSubItemIcon : item.icon;
  const prefix = item.prefix;
  const subItemContentPl = isSubToSub ? 9 : parent ? 3 : 0;
  const navTitleSlug = item.title.toLowerCase().replace(/\s+/g, '-');
  const navTestId = `nav-link-${navTitleSlug}`;
  const isNavLinkActive = () => {
    if (router.pathname === item.path || handleURLQueries(router, item.path)) {
      return true;
    } else {
      return false;
    }
  };
  const ListItem = styled(MuiListItem)<
    ListItemProps & {
      component?: ElementType;
      href: string;
      target?: '_blank' | undefined;
    }
  >(({ theme }) => ({
    width: 'auto',
    paddingTop: theme.spacing(2.25),
    color: theme.palette.text.primary,
    paddingBottom: theme.spacing(2.25),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.active, &.active:hover': {
      backgroundColor: hexToRGBA(theme.palette.primary.main, 0.08),
    },
    '&:focus-visible': {
      outline: 0,
      backgroundColor: theme.palette.action.focus,
    },
  }));

  return (
    <CanViewNavLink navLink={item}>
      <ListItem
        component={Link}
        {...(item.disabled && { tabIndex: -1 })}
        className={clsx({ 'top-nav-link': true, active: isNavLinkActive() })}
        data-testid={navTestId}
        href={item.path === undefined ? '/' : `${item.path}`}
        {...(item.openInNewTab ? { target: '_blank' } : null)}
        onClick={(e) => {
          if (item.path === undefined) {
            e.preventDefault();
            e.stopPropagation();
            item.onClick?.();
          }
          if (navVisible) {
            toggleNavVisibility();
          }
        }}
        sx={(theme) => ({
          width: '100%',
          color: theme.palette.text.primary,
          px: '0 !important',
          py: 2.25,
          ...(item.disabled ? { pointerEvents: 'none' } : { cursor: 'pointer' }),
          pl: `${collapsedIconInsetPx}px`,
          pr: 3.5,
        })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
            ...(subItemContentPl ? { pl: subItemContentPl } : {}),
          }}
        >
          {isSubToSub ? null : (
            <ListItemIcon
              sx={{
                color: 'text.primary',
                minWidth: NAV_ITEM_ICON_WIDTH_PX,
                mr: NAV_ITEM_ICON_LABEL_GAP,
                '& svg': {
                  fontSize: '1.3rem',
                },
              }}
            >
              <FaIconUvilo icon={icon as string} prefix={prefix as IconPrefix} />
            </ListItemIcon>
          )}

          <MenuItemTextMetaWrapper
            sx={{
              ...hiddenNavLabelSx(hideNavLabels),
            }}
          >
            <Typography
              {...((themeConfig.config.menuTextTruncate ||
                (!themeConfig.config.menuTextTruncate && navCollapsed && !navHover)) && {
                noWrap: true,
              })}
            >
              {item.title}
            </Typography>
            {item.badgeContent ? (
              <Chip
                label={item.badgeContent}
                color={item.badgeColor || 'primary'}
                sx={{
                  height: 20,
                  fontWeight: 500,
                  '& .MuiChip-label': { px: 1.5, textTransform: 'capitalize' },
                }}
              />
            ) : null}
          </MenuItemTextMetaWrapper>
        </Box>
      </ListItem>
    </CanViewNavLink>
  );
};

export default VerticalNavLink;
